import { PIN_ITERATIONS } from './securitySettings';

const encoder = new TextEncoder();

const toBase64 = (bytes: Uint8Array) => {
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }
  return btoa(binary);
};

const fromBase64 = (value: string) => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const derivePinHash = async (pin: string, saltBytes: Uint8Array, iterations: number) => {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(pin),
    'PBKDF2',
    false,
    ['deriveBits']
  );

  const salt = new Uint8Array(saltBytes);
  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt.buffer,
      iterations,
      hash: 'SHA-256',
    },

    cryptoKey,
    256
  );

  return toBase64(new Uint8Array(derivedBits));
};

export const normalizePin = (value: string) => value.replace(/\D/g, '');

export const isValidPin = (value: string) => /^\d{4,8}$/.test(normalizePin(value));

export async function createStoredPin(pin: string) {
  const normalizedPin = normalizePin(pin);
  const saltBytes = crypto.getRandomValues(new Uint8Array(16));
  const pinHash = await derivePinHash(normalizedPin, saltBytes, PIN_ITERATIONS);

  return {
    pinHash,
    pinSalt: toBase64(saltBytes),
    pinIterations: PIN_ITERATIONS,
  };
}

export async function verifyStoredPin(
  pin: string,
  stored: { pinHash?: string; pinSalt?: string; pinIterations?: number }
) {
  if (!stored.pinHash || !stored.pinSalt) {
    return false;
  }

  const normalizedPin = normalizePin(pin);
  const derivedHash = await derivePinHash(
    normalizedPin,
    fromBase64(stored.pinSalt),
    stored.pinIterations || PIN_ITERATIONS
  );

  return derivedHash === stored.pinHash;
}
